import { z } from 'zod';

export const PAYMENT_METHODS = [
  'Bank Transfer',
  'Card',
  'Ali Baba or Third Party Site',
  'Other'
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * Whether a website entered in the form is an http(s) URL. A bare domain
 * ("www.example.com") counts: the backend stores it with https://.
 */
export const isWebsite = (value: string): boolean => {
  const url = value.includes('://') ? value : `https://${value}`;

  try {
    const { protocol, hostname } = new URL(url);

    return (
      (protocol === 'http:' || protocol === 'https:') && hostname.includes('.')
    );
  } catch {
    return false;
  }
};

export const supplierApplicationSchema = z.object({
  // Factory information
  factory_name: z.string().min(1, 'Factory name is required'),
  year_established: z
    .number({ invalid_type_error: 'Year established is required' })
    .int()
    .min(1800, 'Enter a valid year')
    .max(new Date().getFullYear(), 'Year cannot be in the future'),
  address: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  factory_size_sqm: z
    .number({ invalid_type_error: 'Must be a number' })
    .int()
    .positive()
    .optional()
    .nullable(),
  number_of_employees: z
    .number({ invalid_type_error: 'Must be a number' })
    .int()
    .positive()
    .optional()
    .nullable(),

  // Production capabilities
  production_capacity: z.string().min(1, 'Production capacity is required'),
  main_product_categories: z
    .string()
    .min(1, 'Main product categories are required'),
  export_markets: z.string().min(1, 'Export markets are required'),
  materials_specialized_in: z
    .string()
    .min(1, 'Materials specialized in are required'),
  moq: z
    .number({ invalid_type_error: 'Must be a number' })
    .int()
    .positive()
    .optional()
    .nullable(),
  sample_lead_time: z.string().optional(),
  bulk_production_lead_time: z.string().optional(),
  certifications: z.string().min(1, 'Certifications are required'),
  preferred_payment_terms: z.string().optional(),
  accepted_payment_methods: z
    .array(z.enum(PAYMENT_METHODS))
    .min(1, 'Please select at least one payment method'),

  // Primary contact
  primary_contact_name: z.string().min(1, 'Primary contact name is required'),
  primary_contact_role: z.string().min(1, 'Primary contact role is required'),
  email: z.string().email('A valid email address is required'),
  // WhatsApp is optional per business decision
  whatsapp_number: z.string().optional(),
  wechat_id: z.string().optional(),

  // Secondary contact (all optional)
  secondary_contact_name: z.string().optional(),
  secondary_contact_role: z.string().optional(),
  additional_email: z
    .string()
    .email('Must be a valid email')
    .optional()
    .or(z.literal('')),
  additional_whatsapp_number: z.string().optional(),
  additional_wechat_id: z.string().optional(),

  // Links
  website: z
    .string()
    .min(1, 'Website is required')
    .refine(isWebsite, 'Enter a valid website, e.g. www.example.com'),
  previous_clients: z.string().min(1, 'Previous clients are required')
});

export type SupplierApplicationFormData = z.infer<
  typeof supplierApplicationSchema
>;
